import {useState} from "react";
import {Link} from "react-router";
import {
  ArrowLeft,
  Database,
  KeyRound,
  LoaderCircle,
  Radio,
  RefreshCw,
  Rocket,
  ShieldCheck,
  StopCircle,
} from "lucide-react";
import {trpc} from "@/admin/lib/trpc";
import {TrialRenewalPanel} from "@/admin/components/TrialRenewalPanel";

type LaunchedTrial = {
  product: "world" | "atlas" | "world-atlas" | "world-tickers";
  version: string;
  subscriptionId: string;
  organizationId: string;
  organizationName: string;
  recipients: string;
  status: string;
  startsAt: string;
  expiresAt: string | null;
  createdAt: string;
  cancellationReason: string | null;
  access: {
    webApi: boolean;
    staticS3: boolean;
    liveS3: boolean;
  };
  activeApiKeys: number;
  iamUserName: string | null;
};

function formatDate(value: string | null): string {
  if (!value) return "Not set";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

function trialState(status: string, expiresAt: string | null) {
  if (status !== "trialing") return {label: "STOPPED", tone: "stopped"};
  if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
    return {label: "EXPIRED", tone: "expired"};
  }
  return {label: "ACTIVE", tone: "active"};
}

export function ActiveTrialsPage() {
  const utils = trpc.useUtils();
  const trials = trpc.admin.listLaunchedTrials.useQuery(undefined, {
    refetchOnWindowFocus: true,
  });
  const [showInactive, setShowInactive] = useState(false);
  const [stoppingId, setStoppingId] = useState<string | null>(null);
  const [trialToStop, setTrialToStop] = useState<LaunchedTrial | null>(null);
  const [trialToRenew, setTrialToRenew] = useState<LaunchedTrial | null>(null);
  const [renewalBusy, setRenewalBusy] = useState(false);
  const stopTrial = trpc.admin.stopLaunchedTrial.useMutation({
    onSettled: () => setStoppingId(null),
    onSuccess: async () => {
      setTrialToStop(null);
      await utils.admin.listLaunchedTrials.invalidate();
    },
  });

  const requestStop = (trial: LaunchedTrial) => {
    if (trialToRenew) return;
    stopTrial.reset();
    setTrialToStop(trial);
  };

  const confirmStop = () => {
    if (!trialToStop) return;
    setStoppingId(trialToStop.subscriptionId);
    stopTrial.mutate({
      subscriptionId: trialToStop.subscriptionId,
      confirmed: true,
    });
  };
  const launchedTrials = (trials.data ?? []) as LaunchedTrial[];
  const visibleTrials = launchedTrials.filter(
    (trial) =>
      showInactive ||
      trialState(trial.status, trial.expiresAt).tone === "active",
  );

  return (
    <div className="active-trials-page">
      <header className="active-trials-header">
        <div>
          <span className="active-trials-kicker">
            <ShieldCheck aria-hidden="true" /> TRIAL CONTROL
          </span>
          <h1>Active trials</h1>
          <p>
            Review every trial launched from admin, see exactly which access
            types are live, extend or reactivate access, and stop trials.
          </p>
        </div>
        <div className="active-trials-header__actions">
          <Link className="active-trials-secondary" to="/">
            <ArrowLeft aria-hidden="true" /> BACK TO ADMIN
          </Link>
          <Link className="active-trials-primary" to="/easy-onboard">
            <Rocket aria-hidden="true" /> LAUNCH TRIAL
          </Link>
        </div>
      </header>

      <section className="active-trials-toolbar">
        <div>
          <strong>{visibleTrials.length}</strong>
          <span>{showInactive ? "ALL TRIALS" : "ACTIVE TRIALS"}</span>
        </div>
        <div className="active-trials-toolbar__controls">
          <label className="active-trials-history-toggle">
            <input
              type="checkbox"
              disabled={Boolean(trialToRenew) || stopTrial.isPending}
              checked={showInactive}
              onChange={(event) => setShowInactive(event.target.checked)}
            />
            <span aria-hidden="true" />
            SHOW EXPIRED / STOPPED
          </label>
          <button
            type="button"
            onClick={() => void trials.refetch()}
            disabled={trials.isFetching}
          >
            <RefreshCw
              className={trials.isFetching ? "active-trials-spin" : ""}
              aria-hidden="true"
            />
            REFRESH
          </button>
        </div>
      </section>

      {trials.isLoading ? (
        <div className="active-trials-empty">
          <LoaderCircle className="active-trials-spin" aria-hidden="true" />
          <strong>Loading launched trials…</strong>
        </div>
      ) : trials.error ? (
        <p className="active-trials-error" role="alert">
          {trials.error.message}
        </p>
      ) : visibleTrials.length ? (
        <div className="active-trials-list">
          {visibleTrials.map((trial) => {
            const state = trialState(trial.status, trial.expiresAt);
            const canStop = trial.status === "trialing";
            const isStopping = stoppingId === trial.subscriptionId;
            return (
              <article className="active-trial-card" key={trial.subscriptionId}>
                <header>
                  <div>
                    <span
                      className={`active-trial-status is-${state.tone}`}
                    >
                      {state.label}
                    </span>
                    <h2>{trial.organizationName}</h2>
                    <p>{trial.recipients}</p>
                  </div>
                  <div className="active-trial-actions">
                    {["trialing", "canceled"].includes(trial.status) ? <button
                      className="active-trial-renew" type="button"
                      disabled={stopTrial.isPending || renewalBusy || Boolean(trialToRenew)}
                      onClick={() => {setTrialToStop(null); stopTrial.reset(); setTrialToRenew(trial);}}
                    ><RefreshCw aria-hidden="true" />{state.tone === "active" ? "EXTEND TRIAL" : "REACTIVATE TRIAL"}</button> : null}
                  {canStop ? (
                    <button
                      className="active-trial-stop"
                      type="button"
                      disabled={stopTrial.isPending || Boolean(trialToRenew)}
                      onClick={() => requestStop(trial)}
                    >
                      {isStopping ? (
                        <LoaderCircle
                          className="active-trials-spin"
                          aria-hidden="true"
                        />
                      ) : (
                        <StopCircle aria-hidden="true" />
                      )}
                      {isStopping ? "REVOKING…" : "STOP TRIAL"}
                    </button>
                  ) : null}
                  </div>
                </header>

                <div className="active-trial-access">
                  <span className="is-enabled">{trial.product === "world-atlas" ? "World + Atlas" : trial.product === "atlas" ? "Atlas" : trial.product === "world-tickers" ? "World (ticker-only)" : "World"} {trial.version}</span>
                  <span className={trial.access.webApi ? "is-enabled" : ""}>
                    <KeyRound aria-hidden="true" /> WEB API
                  </span>
                  <span className={trial.access.staticS3 ? "is-enabled" : ""}>
                    <Database aria-hidden="true" /> HISTORIC ONLY
                  </span>
                  <span className={trial.access.liveS3 ? "is-enabled" : ""}>
                    <Radio aria-hidden="true" /> HISTORIC + CURRENT
                  </span>
                </div>

                <dl>
                  <div>
                    <dt>STARTED</dt>
                    <dd>{formatDate(trial.startsAt)}</dd>
                  </div>
                  <div>
                    <dt>ENDS / STOPPED</dt>
                    <dd>{formatDate(trial.expiresAt)}</dd>
                  </div>
                  <div>
                    <dt>ACTIVE API KEYS</dt>
                    <dd>{trial.activeApiKeys}</dd>
                  </div>
                  <div>
                    <dt>SUBSCRIPTION</dt>
                    <dd title={trial.subscriptionId}>
                      {trial.subscriptionId.slice(0, 12)}…
                    </dd>
                  </div>
                </dl>

                {trial.cancellationReason ? (
                  <p className="active-trial-reason">
                    {trial.cancellationReason}
                  </p>
                ) : null}

                {trialToRenew?.subscriptionId === trial.subscriptionId ? <TrialRenewalPanel key={trial.subscriptionId} trial={trialToRenew} onClose={() => setTrialToRenew(null)} onBusy={setRenewalBusy} /> : null}

                {trialToStop?.subscriptionId === trial.subscriptionId ? (
                  <section
                    className="active-trial-confirm"
                    role="alertdialog"
                    aria-labelledby="active-trial-confirm-title"
                    aria-describedby="active-trial-confirm-description"
                  >
                    <div>
                      <span>CONFIRM REVOCATION</span>
                      <h3 id="active-trial-confirm-title">
                        Stop the {trial.organizationName} trial?
                      </h3>
                      <p id="active-trial-confirm-description">
                        This immediately revokes every API key on the trial and
                        deletes its AWS IAM user and S3 access keys. This cannot
                        be undone. Reactivation creates replacement credentials.
                      </p>
                    </div>
                    <div className="active-trial-confirm__actions">
                      <button
                        type="button"
                        disabled={stopTrial.isPending}
                        onClick={() => {
                          setTrialToStop(null);
                          stopTrial.reset();
                        }}
                      >
                        CANCEL
                      </button>
                      <button
                        className="is-danger"
                        type="button"
                        disabled={stopTrial.isPending}
                        onClick={confirmStop}
                      >
                        {isStopping ? (
                          <LoaderCircle
                            className="active-trials-spin"
                            aria-hidden="true"
                          />
                        ) : (
                          <StopCircle aria-hidden="true" />
                        )}
                        {isStopping ? "REVOKING…" : "YES, REVOKE ACCESS"}
                      </button>
                    </div>
                    {stopTrial.error ? (
                      <p className="active-trials-error" role="alert">
                        {stopTrial.error.message}
                      </p>
                    ) : null}
                  </section>
                ) : null}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="active-trials-empty">
          <ShieldCheck aria-hidden="true" />
          <strong>
            {showInactive
              ? "No trials have been launched yet."
              : "No active trials."}
          </strong>
          {showInactive ? (
            <Link to="/easy-onboard">Launch the first trial</Link>
          ) : launchedTrials.length ? (
            <button type="button" onClick={() => setShowInactive(true)}>
              Show expired / stopped trials
            </button>
          ) : (
            <Link to="/easy-onboard">Launch the first trial</Link>
          )}
        </div>
      )}

      {stopTrial.error && !trialToStop ? (
        <p className="active-trials-error" role="alert">
          {stopTrial.error.message}
        </p>
      ) : null}
    </div>
  );
}
